import React, { useEffect, useState } from 'react';
import axios from 'axios';
import canReassign from './canReassign';
import API_URL from '../../config';
import StatusLabel from './StatusLabel';
import RatingRing from './RatingRing';
import TextCandidateModal from './TextCandidateModal';
import { callNumber } from './rcWidget';
import { canContactCandidate } from './overrideUsers';
import './TimelineDrawer.css';

// Candidate timeline (October 2026): opened from the clock button next to a candidate's name on
// Driver Candidates, Candidate Archive and the Messages thread header. Slides in over the page so
// the recruiter keeps their place. Data: GET /api/candidates/:id/timeline (routes/candidate-activity.js).

// Same solid 24px icon style as the call/text buttons (.call-btn svg)
const ICONS = {
  phone: 'M6.6 10.8c1.4 2.8 3.7 5.1 6.5 6.5l2.2-2.2c.3-.3.7-.4 1-.2 1.1.4 2.3.6 3.5.6.6 0 1 .4 1 1v3.4c0 .6-.4 1-1 1C10.6 21 3 13.4 3 4c0-.6.4-1 1-1h3.5c.6 0 1 .4 1 1 0 1.2.2 2.4.6 3.5.1.3 0 .7-.2 1L6.6 10.8z',
  missed: 'M6.6 10.8c1.4 2.8 3.7 5.1 6.5 6.5l2.2-2.2c.3-.3.7-.4 1-.2 1.1.4 2.3.6 3.5.6.6 0 1 .4 1 1v3.4c0 .6-.4 1-1 1C10.6 21 3 13.4 3 4c0-.6.4-1 1-1h3.5c.6 0 1 .4 1 1 0 1.2.2 2.4.6 3.5.1.3 0 .7-.2 1L6.6 10.8zM15 3l6 6m0-6-6 6',
  text: 'M4 4h16c1.1 0 2 .9 2 2v10c0 1.1-.9 2-2 2H8l-4 4V6c0-1.1.9-2 2-2z',
  status: 'M9 16.2 4.8 12l-1.4 1.4L9 19 21 7l-1.4-1.4z',
  send: 'M2 21 23 12 2 3v7l15 2-15 2z',
  customer: 'M12 7V3H2v18h20V7H12zM6 19H4v-2h2v2zm0-4H4v-2h2v2zm0-4H4V9h2v2zm0-4H4V5h2v2zm4 12H8v-2h2v2zm0-4H8v-2h2v2zm0-4H8V9h2v2zm0-4H8V5h2v2zm10 12h-8v-2h2v-2h-2v-2h2v-2h-2V9h8v10z',
  upload: 'M9 16h6v-6h4l-7-7-7 7h4zm-4 2h14v2H5z',
  shield: 'M12 1 3 5v6c0 5.6 3.8 10.7 9 12 5.2-1.3 9-6.4 9-12V5l-9-4zm-2 16-4-4 1.4-1.4 2.6 2.6 6.6-6.6L18 9l-8 8z',
  flag: 'M14.4 6 14 4H5v17h2v-7h5.6l.4 2h7V6z',
  archive: 'M20.5 5.2 19.1 3.5C18.9 3.2 18.5 3 18 3H6c-.5 0-.9.2-1.2.5L3.5 5.2C3.2 5.6 3 6 3 6.5V19c0 1.1.9 2 2 2h14c1.1 0 2-.9 2-2V6.5c0-.5-.2-.9-.5-1.3zM12 17.5 6.5 12H10v-2h4v2h3.5L12 17.5zM5.1 5l.8-1h12l.9 1H5.1z',
  skip: 'M12 2C6.5 2 2 6.5 2 12s4.5 10 10 10 10-4.5 10-10S17.5 2 12 2zm5 11H7v-2h10v2z',
};

const FILTERS = [
  { key: 'all', label: 'All' },
  { key: 'call', label: 'Calls' },
  { key: 'text', label: 'Texts' },
  { key: 'status', label: 'Status' },
  { key: 'customer', label: 'Customer' },
];

const dayLabel = iso => {
  const d = new Date(iso);
  const today = new Date(); today.setHours(0, 0, 0, 0);
  const that = new Date(d); that.setHours(0, 0, 0, 0);
  const diff = Math.round((today - that) / 86400000);
  if (diff === 0) return 'Today';
  if (diff === 1) return 'Yesterday';
  return d.toLocaleDateString('en-US', { weekday: 'short', day: 'numeric', month: 'short', year: d.getFullYear() === today.getFullYear() ? undefined : 'numeric' });
};
const timeLabel = iso => new Date(iso).toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' });

export default function TimelineDrawer({ candidateId, user, onClose, onContact }) {
  const [data, setData] = useState(null);
  const [error, setError] = useState('');
  const [filter, setFilter] = useState('all');
  const [texting, setTexting] = useState(false);
  const [reloadKey, setReloadKey] = useState(0);

  useEffect(() => {
    let alive = true;
    setError('');
    axios.get(`${API_URL}/api/candidates/${candidateId}/timeline`, { headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } })
      .then(r => { if (alive) setData(r.data); })
      .catch(e => { if (alive) setError(e.response?.data?.error || 'Could not load the timeline.'); });
    return () => { alive = false; };
  }, [candidateId, reloadKey]);

  // New candidate: start from the top with the All filter
  useEffect(() => { setData(null); setFilter('all'); }, [candidateId]);

  useEffect(() => {
    const onKey = e => { if (e.key === 'Escape' && !texting) onClose(); };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [onClose, texting]);

  const undoReassign = async batchId => {
    try {
      const r = await axios.post(`${API_URL}/api/reassign/batch/${batchId}/undo`, {}, { headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } });
      if (r.data.skipped) window.alert('This candidate was moved again since, so it was left alone.');
      setReloadKey(k => k + 1);
      if (onContact) onContact();
    } catch (err) {
      window.alert(err.response?.data?.error || 'Could not undo.');
    }
  };

  const c = data?.candidate;
  const canContact = !!(c?.phone && canContactCandidate(user, c));
  const headers = { Authorization: `Bearer ${localStorage.getItem('token')}` };

  // Same as the table's call button: dial through the RingCentral widget and log call_started so
  // the candidate leaves the Today call list right away; then refresh the timeline.
  const startCall = () => {
    callNumber(c.phone);
    axios.post(`${API_URL}/api/candidates/${c.id}/call-started`, {}, { headers })
      .then(() => { setReloadKey(k => k + 1); if (onContact) onContact(); })
      .catch(() => {});
  };
  const events = (data?.events || []).filter(e => filter === 'all' || e.type === filter);
  let lastDay = '';

  return (
    <div className="tl-scrim" onClick={onClose}>
      <aside className="tl-drawer" onClick={e => e.stopPropagation()}>
        <div className="tl-head">
          <button className="tl-close" onClick={onClose} aria-label="Close">×</button>
          <h3>{c ? c.candidate_name : 'Timeline'}</h3>
          {c && (
            <>
              <div className="tl-sub">
                {c.phone || 'No phone'}{c.company_name ? ` · ${c.company_name}` : ''}{c.recruiter_name ? ` · ${c.recruiter_name.split(' ')[0]}` : ''}
              </div>
              <div className="tl-chips">
                {c.mountain_rating != null && <RatingRing rating={c.mountain_rating} />}
                {c.screening_status && <StatusLabel value={c.screening_status} />}
                {c.bg_status && <span className="tl-chip-label">BGV <StatusLabel value={c.bg_status} /></span>}
                {c.drug_status && <span className="tl-chip-label">Drug <StatusLabel value={c.drug_status} /></span>}
                {c.physical_status && <span className="tl-chip-label">MEC <StatusLabel value={c.physical_status} /></span>}
              </div>
            </>
          )}
        </div>

        <div className="tl-filters">
          {FILTERS.map(f => (
            <button key={f.key} className={`tl-filter${filter === f.key ? ' active' : ''}`} onClick={() => setFilter(f.key)}>{f.label}</button>
          ))}
          {canContact && (
            <span className="tl-contact">
              <button type="button" className="call-btn" title={`Call ${c.candidate_name}`} onClick={startCall}>
                <svg viewBox="0 0 24 24"><path d={ICONS.phone} /></svg>
              </button>
              <button type="button" className="call-btn text-btn" title={`Text ${c.candidate_name}`} onClick={() => setTexting(true)}>
                <svg viewBox="0 0 24 24"><path d={ICONS.text} /></svg>
              </button>
            </span>
          )}
        </div>

        <div className="tl-body">
          {error && <div className="tl-empty">{error}</div>}
          {!data && !error && <div className="tl-empty">Loading…</div>}
          {data && events.length === 0 && <div className="tl-empty">Nothing here yet.</div>}
          {events.map((e, i) => {
            const day = dayLabel(e.at);
            const showDay = day !== lastDay;
            lastDay = day;
            const iconKey = e.icon || 'status';
            return (
              <React.Fragment key={i}>
                {showDay && <div className="tl-day">{day}</div>}
                <div className="tl-event">
                  <div className={`tl-icon tl-icon-${e.missed ? 'missed' : e.warn ? 'warn' : e.type}`}>
                    <svg viewBox="0 0 24 24"><path d={ICONS[iconKey] || ICONS.status} /></svg>
                  </div>
                  <div className="tl-main">
                    <div className="tl-title">{e.title}</div>
                    {e.detail && <div className="tl-detail">{e.detail}</div>}
                    {e.batchId && canReassign(user) && (
                      <button type="button" className="tl-undo" onClick={() => undoReassign(e.batchId)}>Undo</button>
                    )}
                    {e.body && <div className={`tl-bubble ${e.dir === 'out' ? 'out' : 'in'}${e.failed ? ' failed' : ''}`}>{e.body}</div>}
                  </div>
                  <div className="tl-time">{timeLabel(e.at)}</div>
                </div>
              </React.Fragment>
            );
          })}
        </div>
      </aside>
      {texting && c && (
        <div onClick={e => e.stopPropagation()}>
          <TextCandidateModal
            candidate={c}
            onClose={() => { setTexting(false); setReloadKey(k => k + 1); if (onContact) onContact(); }}
          />
        </div>
      )}
    </div>
  );
}
